/**
 * Lucy Proactive Engagement Engine
 * WowStore.live — Lucy Intelligence v6
 *
 * Behavioral trigger engine that auto-opens Lucy chat with contextual
 * messages based on browsing behavior. Increases engagement from ~5%
 * (manual open only) to estimated 15-25% via smart, non-annoying triggers.
 *
 * Triggers (priority order):
 *   1. Exit Intent + Cart Items → rescue message
 *   2. Return Visitor + Never Chatted → personalized intro
 *   3. Deep Browser (5+ product views) → capsule wardrobe offer
 *   4. Collection Page Idle (30s) → category help
 *   5. Product Page Idle (45s) → try-on / styling tip
 *   6. Homepage First Visit (90s) → welcome intro
 *
 * Anti-annoyance:
 *   - Max 1 trigger per session
 *   - 24hr cooldown per trigger type
 *   - Dismiss = session block
 *   - Respects lucy_opted_out flag
 *
 * Integration:
 *   window.LucyAI.openChat() — opens chat panel
 *   window.LucyAI.addMessage() — injects Lucy message
 *   window.LucyAnalytics.track() — fires analytics event
 *   localStorage lucy_profile — user preferences
 */
(function() {
  'use strict';

  /* Prevent duplicate initialization from Shopify section re-renders */
  if (window._lucyProactiveInstance) return;
  window._lucyProactiveInstance = Date.now();

  /* ═══════════════════ Config ═══════════════════ */
  var STORAGE_KEY = 'lucy_proactive';
  var COOLDOWN_HOURS = 24;
  var MAX_TRIGGERS_PER_SESSION = 1;
  var INITIAL_GRACE_PERIOD_MS = 5000; /* 5s — don't trigger immediately on page load */

  /* Trigger timing (ms) */
  var TIMERS = {
    homepage_welcome:   90000,  /* 90s on homepage */
    collection_idle:    30000,  /* 30s on collection page */
    product_idle:       45000,  /* 45s on product page */
    deep_browser:       0,      /* Immediate check when threshold met */
    return_visitor:     8000,   /* 8s after page load */
    exit_intent_cart:   0,      /* On mouse leave (desktop) or back button (mobile) */
    social_arrival:     6000    /* 6s after page load — 1s after grace period ends (5s) so canTrigger doesn't block. Phase 1.5, David lock May 11. */
  };

  /* Social referrer hostnames — Phase 1.5 Lucy auto-greet on arrival from social */
  var SOCIAL_REFERRER_HOSTS = {
    'instagram.com':   'Instagram',
    'l.instagram.com': 'Instagram',
    'www.instagram.com': 'Instagram',
    'tiktok.com':      'TikTok',
    'www.tiktok.com':  'TikTok',
    'facebook.com':    'Facebook',
    'l.facebook.com':  'Facebook',
    'm.facebook.com':  'Facebook',
    'lm.facebook.com': 'Facebook',
    't.co':            'X / Twitter',
    'pinterest.com':   'Pinterest',
    'pin.it':          'Pinterest'
  };

  /* ═══════════════════ State ═══════════════════ */
  var sessionTriggered = false;
  var gracePeriodActive = true;
  var productViewsThisSession = 0;
  var currentPageType = detectPageType();
  var exitIntentBound = false;
  var exitRescueTimer = null;
  var activeTimers = [];

  /* ═══════════════════ Storage ═══════════════════ */

  function getProactiveData() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch(e) { return {}; }
  }

  function saveProactiveData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch(e) { /* storage blocked */ }
  }

  function getUserProfile() {
    try {
      var raw = localStorage.getItem('lucy_profile');
      return raw ? JSON.parse(raw) : {};
    } catch(e) { return {}; }
  }

  function hasOptedOut() {
    try {
      return localStorage.getItem('lucy_opted_out') === 'true';
    } catch(e) { return false; }
  }

  /* ═══════════════════ Cooldown Logic ═══════════════════ */

  function isCoolingDown(triggerType) {
    var data = getProactiveData();
    var cooldowns = data.cooldowns || {};
    var lastFired = cooldowns[triggerType];

    if (!lastFired) return false;

    var elapsed = Date.now() - lastFired;
    return elapsed < (COOLDOWN_HOURS * 60 * 60 * 1000);
  }

  function recordTrigger(triggerType) {
    var data = getProactiveData();
    if (!data.cooldowns) data.cooldowns = {};
    data.cooldowns[triggerType] = Date.now();

    /* Track total triggers for analytics */
    data.totalTriggers = (data.totalTriggers || 0) + 1;
    data.lastTrigger = { type: triggerType, at: Date.now() };

    saveProactiveData(data);
    sessionTriggered = true;
  }

  function hasEverChatted() {
    var data = getProactiveData();
    if (data.hasChatted) return true;

    /* Check if lucy_analytics has any chat_message events */
    try {
      var analytics = localStorage.getItem('lucy_analytics');
      if (analytics) {
        var events = JSON.parse(analytics);
        return events.some(function(e) {
          return e.event === 'chat_message' || e.event === 'chat_open';
        });
      }
    } catch(e) { /* ignore */ }

    return false;
  }

  function markAsChatted() {
    var data = getProactiveData();
    data.hasChatted = true;
    saveProactiveData(data);
  }

  /* ═══════════════════ Page Detection ═══════════════════ */

  function detectPageType() {
    var path = window.location.pathname;

    if (path === '/' || path === '') return 'homepage';
    if (path.indexOf('/products/') === 0) return 'product';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path === '/cart') return 'cart';
    if (path.indexOf('/pages/') === 0) return 'page';
    if (path.indexOf('/search') === 0) return 'search';
    if (path.indexOf('/blogs/') === 0 || path.indexOf('/articles/') === 0) return 'blog';
    return 'other';
  }

  function getCollectionName() {
    var path = window.location.pathname;
    var match = path.match(/\/collections\/([^?#\/]+)/);
    if (!match) return null;

    /* Convert handle to readable name */
    return match[1]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  function getProductTitle() {
    /* Try meta tag first (most reliable) */
    var metaTitle = document.querySelector('meta[property="og:title"]');
    if (metaTitle) {
      var title = metaTitle.getAttribute('content');
      if (title) return title.split(' - ')[0].split(' | ')[0].substring(0, 50);
    }

    /* Fallback to h1 */
    var h1 = document.querySelector('.product__title, h1.product-title, h1');
    if (h1) return h1.textContent.trim().substring(0, 50);

    return null;
  }

  function getCartItemCount() {
    /* Try common Shopify cart count elements */
    var selectors = [
      '.cart-count',
      '.cart-count-bubble span',
      '[data-cart-count]',
      '.header__cart-count',
      '.js-cart-count',
      '#CartCount'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) {
        var count = parseInt(el.textContent.trim(), 10);
        if (!isNaN(count) && count > 0) return count;

        /* Check data attribute */
        var dataCount = parseInt(el.getAttribute('data-cart-count'), 10);
        if (!isNaN(dataCount) && dataCount > 0) return dataCount;
      }
    }

    return 0;
  }

  /* ═══════════════════ Trigger Check ═══════════════════ */

  function canTrigger(triggerType) {
    /* Global checks */
    if (sessionTriggered) return false;
    if (gracePeriodActive) return false;
    if (hasOptedOut()) return false;

    /* Check if LucyAI is available */
    if (!window.LucyAI || !window.LucyAI.openChat) return false;

    /* Check if chat is already open */
    var chatContainer = document.getElementById('aiChatContainer');
    if (chatContainer && chatContainer.style.display !== 'none' &&
        chatContainer.classList.contains('open')) return false;
    /* Some themes use different selectors */
    if (chatContainer && chatContainer.offsetHeight > 100) return false;

    /* Per-trigger cooldown */
    if (isCoolingDown(triggerType)) return false;

    return true;
  }

  /* ═══════════════════ Message Templates ═══════════════════ */

  function buildMessage(triggerType, context) {
    context = context || {};
    var profile = getUserProfile();
    var styles = (profile.styles || []).join(', ');
    var name = profile.name || '';

    switch (triggerType) {

      case 'exit_intent_cart':
        var cartCount = context.cartCount || 0;
        var messages = [
          "Wait, darling! You have " + cartCount + " item" + (cartCount > 1 ? 's' : '') + " waiting for you. Want me to help you complete your look before you go?",
          "Before you leave " + (name ? name + '! ' : '- ') + "I noticed your cart has some beautiful pieces. Should I suggest what goes perfectly with them?",
          "Hold on! Your cart is looking great. Use code WELCOME15 for 15% off your first order!"
        ];
        return messages[Math.floor(Math.random() * messages.length)];

      case 'return_visitor':
        if (styles) {
          return "Hey" + (name ? ' ' + name : '') + "! Welcome back. I remember you love " + styles + " style. I've spotted some new arrivals that are totally you. Want to see?";
        }
        return "Welcome back" + (name ? ' ' + name : '') + "! I'm Lucy, your personal stylist. I noticed you've visited before but we haven't chatted yet. Can I help you find something special?";

      case 'deep_browser':
        var count = context.productViews || 5;
        var messages2 = [
          "I love your taste! You've been exploring " + count + " pieces. Want me to build you a complete outfit from your favorites?",
          "You've got a great eye, darling! I noticed you've viewed " + count + " products. Shall I put together a capsule wardrobe that ties them all together?",
          "Shopping mode activated! With " + count + " products viewed, I bet I can find the perfect combination. Want me to style a complete look?"
        ];
        return messages2[Math.floor(Math.random() * messages2.length)];

      case 'collection_idle':
        var collection = context.collectionName || 'this collection';
        return "Looking for something specific in " + collection + "? I can filter by your size, style, and budget in seconds. Just tell me what you're after!";

      case 'product_idle':
        var product = context.productTitle;
        if (product) {
          var messages3 = [
            "Love that " + product + "! Want to see how it looks? I can show you styling ideas, or open it in your Dressing Room so you can see yourself in it.",
            "Great choice! " + product + " is a beautiful piece. Want me to suggest what to pair it with for a complete look?",
            "That " + product + " caught your eye, didn't it? I can help you decide — sizing tips, styling ideas, or a full outfit."
          ];
          return messages3[Math.floor(Math.random() * messages3.length)];
        }
        return "Need help deciding? I can show you styling ideas, sizing tips, or open this piece in your Dressing Room to see it on you.";

      case 'homepage_welcome':
        if (styles) {
          return "Hey there! I'm Lucy, your personal AI stylist. Since you love " + styles + " style, let me show you what's trending this week!";
        }
        return "Hey there! I'm Lucy, your personal AI stylist at WowStore. I can help you find your perfect look, build capsule wardrobes, or even try things on virtually. What are you in the mood for?";

      case 'social_arrival':
        /* Phase 1.5 — David lock May 11: "exclusive Lucy welcoming experience, Hi welcome to wowstore.live..."
           1% Movement positioning, Disney-utopia register, customer-as-hero. */
        var source = context.source || 'social';
        var greetings = [
          "Hi — welcome to wowstore.live. A pleasure to be of your service. I see you came from our " + source + ". Small choices × millions of us = real change. Want me to show you exactly where your purchase makes an impact?",
          "Welcome to wowstore.live 🌱 Glad you found us via " + source + ". You're now one tap from the 1% Movement — want to see how it works?",
          "Hi, lovely to have you here. You came from " + source + " — that means you already know we don't preach, we just make giving while looking good very simple. Want a quick tour?",
          "Welcome. " + source + " brought you to us — perfect timing. Small choice. Big change. Want me to show you a piece you might love?"
        ];
        return greetings[Math.floor(Math.random() * greetings.length)];

      default:
        return "Hey! I'm Lucy, your personal stylist. Need help finding something perfect?";
    }
  }

  /* ═══════════════════ Fire Trigger ═══════════════════ */

  function fireTrigger(triggerType, context) {
    if (!canTrigger(triggerType)) return false;

    var message = buildMessage(triggerType, context);

    /* Mobile: the full chat panel is a full-screen takeover — too intrusive on
       landing (Clarity recordings showed it covering the PDP). Show the greeting
       in the teaser BUBBLE instead; the user taps the bubble to open Lucy when
       they want. Desktop keeps the side-panel auto-open. (2026-06-27 mobile-UX) */
    var isMobileLucy = (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) || (window.innerWidth && window.innerWidth <= 768);
    if (isMobileLucy) {
      try {
        var tMsg = document.getElementById('aiChatTeaserMsg');
        var tBub = document.getElementById('aiChatTeaser');
        if (tMsg) tMsg.textContent = message;
        if (tBub) { tBub.style.display = ''; tBub.classList.add('show'); }
      } catch(e) { /* no teaser present — just don't take over the screen */ }
      recordTrigger(triggerType);
      if (window.LucyAnalytics) {
        window.LucyAnalytics.track('proactive_trigger', { trigger_type: triggerType, page_type: currentPageType, device: 'mobile_teaser', page: window.location.pathname });
      }
      return true;
    }

    /* Desktop: open the side chat panel */
    try {
      window.LucyAI.openChat();
    } catch(e) {
      console.warn('[Lucy Proactive] Failed to open chat:', e.message);
      return false;
    }

    /* Wait for chat to render, then inject message */
    setTimeout(function() {
      try {
        /* Add as Lucy message (not user message) */
        if (window.LucyAI.addMessage) {
          window.LucyAI.addMessage(message, 'ai');
        }
      } catch(e) {
        console.warn('[Lucy Proactive] Failed to add message:', e.message);
      }
    }, 500);

    /* Record trigger */
    recordTrigger(triggerType);

    /* Track analytics */
    if (window.LucyAnalytics) {
      window.LucyAnalytics.track('proactive_trigger', {
        trigger_type: triggerType,
        page_type: currentPageType,
        page: window.location.pathname,
        product_views: productViewsThisSession,
        cart_items: getCartItemCount()
      });
    }

    console.log('[Lucy Proactive] Fired trigger:', triggerType);
    return true;
  }

  /* ═══════════════════ Trigger: Exit Intent + Cart ═══════════════════ */

  function setupExitIntentTrigger() {
    if (exitIntentBound) return;
    exitIntentBound = true;

    /* Desktop: mouse leaves viewport at top */
    document.addEventListener('mouseout', function(e) {
      if (e.clientY > 50) return; /* Only trigger when cursor goes to top of viewport */
      if (e.relatedTarget || e.toElement) return; /* Not actually leaving */

      var cartCount = getCartItemCount();
      if (cartCount > 0) {
        fireTrigger('exit_intent_cart', { cartCount: cartCount });
      }
    });

    /* Mobile: back button / visibility change with cart items */
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        var cartCount = getCartItemCount();
        if (cartCount > 0 && !sessionTriggered) {
          /* Store intent — show on return */
          var data = getProactiveData();
          data.pendingExitRescue = { cartCount: cartCount, at: Date.now() };
          saveProactiveData(data);
        }
      } else if (document.visibilityState === 'visible') {
        /* User came back — check for pending rescue */
        var data2 = getProactiveData();
        var rescue = data2.pendingExitRescue;
        if (rescue) {
          var elapsed = Date.now() - (rescue.at || 0);
          if (elapsed < 300000) { /* Within 5 minutes */
            /* Capture the count NOW — pendingExitRescue is deleted below, before
               the timer runs (reading it inside the timer threw on every return
               to the tab). One pending rescue timer at a time. (2026-09-17) */
            var rescueCartCount = rescue.cartCount || 0;
            if (exitRescueTimer) clearTimeout(exitRescueTimer);
            exitRescueTimer = setTimeout(function() {
              exitRescueTimer = null;
              fireTrigger('exit_intent_cart', { cartCount: rescueCartCount });
            }, 2000);
          }
          delete data2.pendingExitRescue;
          saveProactiveData(data2);
        }
      }
    });
  }

  /* ═══════════════════ Trigger: Return Visitor ═══════════════════ */

  function setupReturnVisitorTrigger() {
    var profile = getUserProfile();
    var visitCount = profile.visitCount || 0;

    /* Only trigger for visitors with 3+ visits who never chatted */
    if (visitCount < 3) return;
    if (hasEverChatted()) return;

    setTimeout(function() {
      fireTrigger('return_visitor');
    }, TIMERS.return_visitor);
  }

  /* ═══════════════════ Trigger: Deep Browser ═══════════════════ */

  function trackProductView() {
    if (currentPageType !== 'product') return;

    productViewsThisSession++;

    /* Store in session */
    try {
      var count = parseInt(sessionStorage.getItem('lucy_product_views') || '0', 10);
      count++;
      sessionStorage.setItem('lucy_product_views', String(count));
      productViewsThisSession = count;
    } catch(e) { /* ignore */ }

    /* Check threshold */
    if (productViewsThisSession >= 5) {
      setTimeout(function() {
        fireTrigger('deep_browser', { productViews: productViewsThisSession });
      }, 3000); /* Small delay after 5th view */
    }
  }

  /* ═══════════════════ Trigger: Collection Idle ═══════════════════ */

  function setupCollectionIdleTrigger() {
    if (currentPageType !== 'collection') return;

    var timer = setTimeout(function() {
      var collectionName = getCollectionName();
      fireTrigger('collection_idle', { collectionName: collectionName });
    }, TIMERS.collection_idle);

    activeTimers.push(timer);
  }

  /* ═══════════════════ Trigger: Product Page Idle ═══════════════════ */

  function setupProductIdleTrigger() {
    if (currentPageType !== 'product') return;

    var timer = setTimeout(function() {
      var productTitle = getProductTitle();
      fireTrigger('product_idle', { productTitle: productTitle });
    }, TIMERS.product_idle);

    activeTimers.push(timer);
  }

  /* ═══════════════════ Trigger: Homepage Welcome ═══════════════════ */

  function setupHomepageWelcomeTrigger() {
    if (currentPageType !== 'homepage') return;

    /* Only for first-time or rarely visiting users */
    var profile = getUserProfile();
    var visitCount = profile.visitCount || 0;
    if (visitCount > 5 && hasEverChatted()) return; /* Experienced users who chat — leave them alone */

    var timer = setTimeout(function() {
      fireTrigger('homepage_welcome');
    }, TIMERS.homepage_welcome);

    activeTimers.push(timer);
  }

  /* ═══════════════════ Trigger: Social Arrival (Phase 1.5, May 11) ═══════════════════ */

  function detectSocialReferrerSource() {
    /* Returns "Instagram" / "TikTok" / "Facebook" / etc. if document.referrer
       matches a known social host, else null. Also detects from URL hash
       (#src=instagram) for cases when referrer is empty (e.g. Stories tap-through). */
    try {
      var ref = document.referrer || '';
      if (ref) {
        var host = '';
        try { host = new URL(ref).hostname.toLowerCase(); } catch(e) {}
        if (host && SOCIAL_REFERRER_HOSTS[host]) {
          return SOCIAL_REFERRER_HOSTS[host];
        }
      }
      /* Hash fallback — e.g. /pages/wowstore-live#src=instagram */
      var hash = (window.location.hash || '').toLowerCase();
      var m = hash.match(/[#&]src=([a-z]+)/);
      if (m) {
        var token = m[1];
        if (token === 'ig' || token === 'instagram') return 'Instagram';
        if (token === 'tt' || token === 'tiktok')    return 'TikTok';
        if (token === 'fb' || token === 'facebook')  return 'Facebook';
        if (token === 'pin' || token === 'pinterest') return 'Pinterest';
      }
    } catch(e) { /* defensive */ }
    return null;
  }

  function setupSocialArrivalTrigger() {
    /* Phase 1.5 — David lock May 11: every link from social gets an
       "exclusive Lucy welcoming experience". Fires after grace period
       on ANY page when referrer matches a social host. */
    var source = detectSocialReferrerSource();
    if (!source) return;

    /* Don't double-fire if user has already chatted today */
    if (hasEverChatted() && isCoolingDown('social_arrival')) return;

    var timer = setTimeout(function() {
      /* Build personalized greeting */
      var message = buildMessage('social_arrival', { source: source, page: currentPageType });

      /* PRIMARY: update the teaser bubble text directly so Lucy's
         personalized greeting is visible without forcing the full chat
         panel open (less intrusive UX, more aligned with David's
         "exclusive Lucy welcoming experience"). */
      try {
        var teaserMsg = document.getElementById('aiChatTeaserMsg');
        var teaser = document.getElementById('aiChatTeaser');
        if (teaserMsg) {
          teaserMsg.textContent = message;
        }
        if (teaser) {
          teaser.style.display = '';
          teaser.classList.add('show');
          /* Subtle attention-getter: brief scale-up animation */
          teaser.style.transition = 'transform 0.3s ease';
          teaser.style.transform = 'scale(1.05)';
          setTimeout(function(){ teaser.style.transform = 'scale(1)'; }, 400);
        }
      } catch(e) {
        console.warn('[Lucy Proactive] social_arrival teaser update failed:', e.message);
      }

      /* SECONDARY: still call fireTrigger so cooldowns + analytics record this.
         If openChat actually does open the full panel for some users, even better. */
      if (canTrigger('social_arrival')) {
        recordTrigger('social_arrival');
        if (window.LucyAnalytics) {
          window.LucyAnalytics.track('proactive_trigger', {
            trigger_type: 'social_arrival',
            source: source,
            page: currentPageType,
            page_path: window.location.pathname
          });
        }
        console.log('[Lucy Proactive] social_arrival fired: source=' + source);
      }
    }, TIMERS.social_arrival);

    activeTimers.push(timer);
  }

  /* ═══════════════════ Chat Open Listener ═══════════════════ */

  function watchForManualChatOpen() {
    /* If user opens chat manually, mark as chatted and cancel all triggers */
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.type === 'attributes') {
          var el = m.target;
          if (el.id === 'aiChatContainer' && el.offsetHeight > 100) {
            markAsChatted();
            /* Cancel pending triggers since user is already engaged */
            cancelAllTimers();
          }
        }
      });
    });

    var chatContainer = document.getElementById('aiChatContainer');
    if (chatContainer) {
      observer.observe(chatContainer, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  }

  function cancelAllTimers() {
    activeTimers.forEach(function(t) { clearTimeout(t); });
    activeTimers = [];
  }

  /* ═══════════════════ Initialization ═══════════════════ */

  function init() {
    /* Safety checks */
    if (hasOptedOut()) {
      console.log('[Lucy Proactive] User opted out — inactive');
      return;
    }

    /* Don't run on cart/checkout pages */
    if (currentPageType === 'cart') {
      /* Only set up exit intent on cart page */
      setupExitIntentTrigger();
      console.log('[Lucy Proactive] Cart page — exit intent only');
      return;
    }

    /* Load session product views */
    try {
      productViewsThisSession = parseInt(sessionStorage.getItem('lucy_product_views') || '0', 10);
    } catch(e) { productViewsThisSession = 0; }

    /* Track this product view */
    trackProductView();

    /* Grace period — don't trigger in first 5 seconds */
    setTimeout(function() {
      gracePeriodActive = false;
    }, INITIAL_GRACE_PERIOD_MS);

    /* Set up all triggers */
    setupExitIntentTrigger();
    setupReturnVisitorTrigger();
    setupCollectionIdleTrigger();
    setupProductIdleTrigger();
    setupHomepageWelcomeTrigger();
    setupSocialArrivalTrigger();  /* Phase 1.5 — May 11, David lock — Lucy auto-greet on social arrivals */

    /* Watch for manual chat opens */
    watchForManualChatOpen();

    /* Check if deep browser threshold already exceeded */
    if (productViewsThisSession >= 5) {
      setTimeout(function() {
        fireTrigger('deep_browser', { productViews: productViewsThisSession });
      }, INITIAL_GRACE_PERIOD_MS + 3000);
    }

    console.log('[Lucy Proactive] Engine loaded — page:', currentPageType,
      '| views:', productViewsThisSession,
      '| session triggered:', sessionTriggered);
  }

  /* ═══════════════════ Public API ═══════════════════ */

  window.LucyProactive = {
    /** Manually fire a trigger (for testing) */
    fire: function(type, ctx) {
      gracePeriodActive = false;
      sessionTriggered = false;
      return fireTrigger(type, ctx);
    },

    /** Get proactive data (triggers, cooldowns) */
    getData: getProactiveData,

    /** Reset all cooldowns */
    reset: function() {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('lucy_product_views');
      sessionTriggered = false;
      gracePeriodActive = false;
      productViewsThisSession = 0;
      console.log('[Lucy Proactive] Reset — all cooldowns cleared');
    },

    /** Opt out of proactive triggers */
    optOut: function() {
      localStorage.setItem('lucy_opted_out', 'true');
      cancelAllTimers();
      console.log('[Lucy Proactive] User opted out');
    },

    /** Get current state */
    getState: function() {
      return {
        pageType: currentPageType,
        productViews: productViewsThisSession,
        sessionTriggered: sessionTriggered,
        gracePeriodActive: gracePeriodActive,
        hasChatted: hasEverChatted(),
        cartItems: getCartItemCount(),
        activeTimers: activeTimers.length,
        proactiveData: getProactiveData()
      };
    }
  };

  /* ═══════════════════ Start ═══════════════════ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    /* Delay slightly to ensure LucyAI is loaded */
    setTimeout(init, 1000);
  }

})();
